<script>
    import Modal from '@/components/modals/Modal';
    import ButtonSpinner from '@/components/shared/ButtonSpinner';
    import FormSelect from '@/components/shared/form/FormSelect';
    import ModalService from '@/services/core/modal.service';
    import tinyPumperIncidentsService from '@/services/tinyPumperIncidents.service';
    import { SENSOR_SNOOZE_REASON_LABELS, SensorSnoozeReason, toSelectOptions } from '@/enums/TinyPumper';

    const DEFAULT_SNOOZE_DAYS = 7;

    /**
     * Silences one incident until a date.
     *
     * Expiry is required and must be in the future: a snooze with no end is a Hold On, and the two
     * are deliberately different things — Hold On is permanent and customer-visible, a snooze is
     * ops-side, time-boxed, and records who decided it.
     */
    export default {
        name: 'HealthCheckSnoozeModal',
        props: {
            modalData: Object
        },
        components: {
            Modal,
            ButtonSpinner,
            FormSelect
        },
        data() {
            return {
                isLoading: 0,
                item: {
                    reason: this.modalData?.reason ?? SensorSnoozeReason.WaitingOnClient,
                    snoozedUntil: '',
                    note: ''
                }
            };
        },
        computed: {
            getReasonOptions() {
                return toSelectOptions(SENSOR_SNOOZE_REASON_LABELS);
            },
            getIncident() {
                return this.modalData?.incident || {};
            },
            // The Today queue opens this pre-set as "Visit booked" or "Waiting on client".
            getTitle() {
                return this.modalData?.title || 'Snooze incident';
            },
            getUntilLabel() {
                return this.modalData?.untilLabel || 'Snooze until';
            },
            getSubmitText() {
                return this.modalData?.submitText || 'Snooze';
            },
            getMinDate() {
                return this.toInputDate(new Date());
            }
        },
        methods: {
            toInputDate(date) {
                return date.toISOString().slice(0, 10);
            },
            close() {
                ModalService.closeModal(true);
            },
            submit() {
                this.isLoading++;

                // Sent as an end-of-day UTC instant: a date-only value would expire the snooze at
                // midnight of the day the operator picked, a day earlier than they meant.
                const snoozedUntil = `${this.item.snoozedUntil}T23:59:59Z`;

                tinyPumperIncidentsService
                    .snooze(this.getIncident.id, {
                        // FormSelect hands back the option value as a string; the API binds an int enum.
                        reason: Number(this.item.reason),
                        snoozedUntil,
                        note: this.item.note
                    })
                    .then(() => {
                        this.modalData?.onSaveSuccess?.();
                        ModalService.closeModal();
                    })
                    .finally(() => this.isLoading--);
            }
        },
        mounted() {
            const until = new Date();
            until.setDate(until.getDate() + (this.modalData?.days ?? DEFAULT_SNOOZE_DAYS));
            this.item.snoozedUntil = this.toInputDate(until);
        }
    };
</script>

<template>
    <Modal sizeClass="modal-md-plus">
        <form @submit.prevent="submit">
            <div class="modal-content">
                <div class="modal-header">
                    <h3 class="modal-title">{{ getTitle }}</h3>
                </div>
                <div class="modal-body">
                    <p class="text-muted">
                        <span class="fw-bold">{{ getIncident.companyName || '—' }}</span>
                        <span v-if="getIncident.batteryName"> · {{ getIncident.batteryName }}</span>
                        <span v-if="getIncident.affectedSensorCount"> · {{ getIncident.affectedSensorCount }} sensor(s)</span>
                    </p>

                    <div class="form-group mb-4">
                        <label class="form-label h5">Reason</label>
                        <FormSelect v-model="item.reason" :options="getReasonOptions" required />
                    </div>

                    <div class="form-group mb-4">
                        <label class="form-label h5">{{ getUntilLabel }}</label>
                        <input type="date" class="form-control" v-model="item.snoozedUntil" :min="getMinDate" required />
                        <small class="text-muted">The incident goes loud again after this date.</small>
                    </div>

                    <div class="form-group mb-4">
                        <label class="form-label h5">Note</label>
                        <textarea class="form-control" rows="3" maxlength="500" v-model="item.note"></textarea>
                        <small class="text-muted">What the next person needs to know. Recorded with your name and the time.</small>
                    </div>
                </div>
                <div class="modal-footer">
                    <div class="d-flex gap-2 ms-auto">
                        <button type="button" class="btn" @click="close">Cancel</button>
                        <ButtonSpinner :isLoading="isLoading" :text="getSubmitText" />
                    </div>
                </div>
            </div>
        </form>
    </Modal>
</template>
